'use es6';

import { Map as ImmutableMap } from 'immutable';
import { CUSTOM, TABLE } from 'reporting-data/constants/chartTypes';
import { Dataset } from 'reporting-data/v2/dataset/datasetRecords';
import { generateDrilldownName, generateDrilledDefinition } from 'reporting-snowflake/relational/enhance/drilldown/generate';
import { getIsReportFlpRestricted } from 'reporting-snowflake/relational/enhance/drilldown/drilldown-utils';
import { visualize } from 'reporting-snowflake/relational/visualize/visualize';
import { withEvents } from 'reporting-snowflake/relational/visualize/with-events';
import { sanitizeVizForMobile } from '../experimental/sanitizeVizForMobile';
import { API_LIMIT } from '../report-options';
import usePromiseEffect from '../../shared/hooks/usePromiseEffect';
import { useDeps } from '../useDeps';
import { fiscalYearFetcher } from '../../PlatformReport/FiscalYearProvider';
import { DEFAULT_FISCAL_YEAR_MONTH_NAME } from 'reporting-data/constants/dates';
import { useFetchSnowflakeBrandKitColorPalettes } from 'reporting-snowflake/relational/utils/brand-kit-color-palette-utils';
import { getOnTableCellLinkClickCallback } from '../../../lib/usageTrackerUtils';
import { getRelationalReportColorOptions, getRelationalReportY1ColorOptions, getRelationalReportY2ColorOptions } from 'reporting-data/report/relationalReportGetters';
import { BRAND_KIT_PALETTE_ID } from 'reporting-snowflake/relational/visualize/color/palettes';
import { useFilterRelevantUserInfo } from 'reporting-snowflake/shared/lib/utils';
import { useReportingPalettes } from 'reporting-visualizations/hooks/useReportingPalettes';
const applyLimitAndOffset = (reportOptions, dataset) => {
  const {
    limit,
    offset
  } = reportOptions;
  const {
    key,
    data,
    properties,
    paginationSize,
    searchDimension,
    insightMeta,
    insightOption
  } = dataset;
  const clampedOffset = offset % API_LIMIT; // TODO remove when data set actually accumulates

  return Dataset({
    key,
    data: limit ? data.skip(clampedOffset).take(limit) : data.skip(clampedOffset),
    properties,
    paginationSize,
    searchDimension,
    insightMeta,
    insightOption
  });
};
const createDrilldownHandler = (onDrilldown, dataset, reportDefinition, deps, fiscalYearMonthName, userGates) => {
  return clickEvent => {
    const dataRow = clickEvent.row;
    const drilldownName = generateDrilldownName(clickEvent, dataset, reportDefinition);
    const drilledDefinition = generateDrilledDefinition(dataRow, deps, reportDefinition, fiscalYearMonthName, userGates);
    const {
      meta
    } = deps;
    const flpRestricted = getIsReportFlpRestricted(reportDefinition, meta);
    const drilledReport = ImmutableMap({
      reportDefinition: drilledDefinition,
      name: drilldownName,
      chartType: CUSTOM,
      flpRestricted
    });
    onDrilldown(drilledReport);
  };
};
const useFiscalYearSetting = () => {
  const {
    data,
    error
  } = usePromiseEffect(fiscalYearFetcher);
  if (error) {
    return DEFAULT_FISCAL_YEAR_MONTH_NAME;
  }
  return data;
};
const useVisualize = (processedDatasets, reportWithOptions,
// eslint-disable-next-line @typescript-eslint/no-unused-vars
reportMeta, onDrilldown, onSort, isMobile, gates, compact, mobileReportingOptions = {}) => {
  var _ref, _colorOptions$get;
  const fiscalYearMonthName = useFiscalYearSetting();
  const {
    userGates
  } = useFilterRelevantUserInfo();
  const colorOptions = getRelationalReportColorOptions(reportWithOptions);
  const y1ColorOptions = getRelationalReportY1ColorOptions(reportWithOptions);
  const y2ColorOptions = getRelationalReportY2ColorOptions(reportWithOptions);
  const paletteId = (_ref = (_colorOptions$get = colorOptions === null || colorOptions === void 0 ? void 0 : colorOptions.get('paletteId')) !== null && _colorOptions$get !== void 0 ? _colorOptions$get : y1ColorOptions === null || y1ColorOptions === void 0 ? void 0 : y1ColorOptions.get('paletteId')) !== null && _ref !== void 0 ? _ref : y2ColorOptions === null || y2ColorOptions === void 0 ? void 0 : y2ColorOptions.get('paletteId');
  const shouldDeferBrandKitFetch = paletteId !== BRAND_KIT_PALETTE_ID;
  const {
    isLoading: isBrandKitLoading,
    snowflakeBrandKitPaletteMap: brandKitPalettes
  } = useFetchSnowflakeBrandKitColorPalettes(undefined, shouldDeferBrandKitFetch);
  const {
    loading,
    data: deps
  } = useDeps(reportWithOptions.reportDefinition);
  const palettes = useReportingPalettes();
  if (loading || isBrandKitLoading) {
    return undefined;
  }
  if (!processedDatasets) {
    return undefined;
  }
  const processedPrimaryDataset = processedDatasets.get('primary');
  if (!processedPrimaryDataset) {
    return undefined;
  }
  if (!fiscalYearMonthName) {
    return undefined;
  }
  const {
    reportDefinition,
    reportOptions
  } = reportWithOptions;
  const clampedDatasets = processedDatasets.mapEntries(([key, processedDataset]) => {
    return [key, applyLimitAndOffset(reportOptions, processedDataset)];
  });
  let visualization = visualize(reportDefinition, clampedDatasets, reportOptions, {
    fiscalYearMonthName,
    brandKitPalettes,
    gates,
    compact,
    onTableCellLinkClick: getOnTableCellLinkClickCallback(reportWithOptions),
    palettes
  });
  const handleDrilldown = onDrilldown && fiscalYearMonthName ? createDrilldownHandler(onDrilldown, processedPrimaryDataset, reportDefinition, deps, fiscalYearMonthName, userGates) : undefined;
  const handleSort = visualization.type === TABLE ? onSort : undefined;
  visualization = withEvents(ImmutableMap({
    onPointClick: handleDrilldown,
    onSort: handleSort
  }), visualization, reportDefinition.columns);
  visualization = sanitizeVizForMobile(isMobile, visualization, mobileReportingOptions);
  return visualization;
};
export default useVisualize;